import { getChannelEnvironmentEgressMode } from "@arti/runtime-shared/channelEnvironmentEgress";
import { assertUnreachable } from "@arti/utilities";

import type {
  HudPillSignature,
  HudStatusTone,
} from "../../common/HudStatusPill";
import type {
  ChannelEnvironment,
  ChannelEnvironmentFormValues,
  ChannelEnvironmentPatchResponse,
} from "../useChannelEnvironment";
import type { ChannelWorkspaceRepository } from "../useChannelWorkspaceRepositories";

export function getRepositoryDetachState(
  repository: ChannelWorkspaceRepository,
): "acknowledgement" | "blocked" | "queued" | "retry" | "waiting" | null {
  if (repository.status !== "detach_pending") {
    return null;
  }
  const blocker = repository.detachBlocker;
  switch (blocker) {
    case "checkout_absent": {
      return "acknowledgement";
    }
    case "checkout_not_git":
    case "dirty_checkout":
    case "unpushed_branches":
    case "dirty_and_unpushed": {
      return "blocked";
    }
    case "verification_failed":
    case "removal_failed": {
      return "retry";
    }
    case "active_spawns":
    case "runtime_unavailable": {
      return "waiting";
    }
    case "queued":
    case null: {
      return "queued";
    }
    default: {
      throw assertUnreachable(blocker);
    }
  }
}

export function formValuesFromEnvironment(
  environment: ChannelEnvironment,
): ChannelEnvironmentFormValues {
  return {
    actorProcessToolsEnabled: environment.actorProcessToolsEnabled,
    cpuCount: environment.cpuCount,
    idleStopAfterHours: environment.idleStopAfterHours,
    maxConcurrentSpawns: environment.maxConcurrentSpawns,
    maxSpawnWallSeconds: environment.maxSpawnWallSeconds,
    previewPorts: environment.previewPorts,
    profileId: environment.profileId,
    ramMB: environment.ramMB,
    retentionDaysAfterChannelKill: environment.retentionDaysAfterChannelKill,
    storageMB: environment.storageMB,
  };
}

export function getStatusTone(status: string | null): HudStatusTone {
  switch (status) {
    case "running": {
      return "success";
    }
    case "archived":
    case "hibernated":
    case "hibernating":
    case "retained":
    case "removing": {
      return "warning";
    }
    case "errored": {
      return "danger";
    }
    case null:
    case "attaching":
    case "gced":
    case "resuming":
    case "stopped": {
      return "neutral";
    }
    default: {
      return "info";
    }
  }
}

/** Motion signature for the status dot, keyed off the same states as
 *  getStatusTone. Live/transitional states get a distinct rhythm; resting
 *  states (hibernated/retained/archived/stopped/…) stay static. */
export function getStatusMotion(status: string | null): HudPillSignature {
  switch (status) {
    case "running": {
      return "pulse";
    }
    case "hibernating": {
      return "breathe";
    }
    case "attaching":
    case "resuming": {
      return "queued";
    }
    case "errored": {
      return "blink";
    }
    default: {
      return "none";
    }
  }
}

export function getStatusLabel(environment: ChannelEnvironment): string {
  if (environment.status === "archived") {
    return "archived · legacy partial";
  }
  if (environment.status === "retained") {
    return "retained · legacy lossy";
  }
  if (environment.status !== "restarting") {
    return environment.status;
  }
  return `restarting · ${formatElapsed(environment.restartStartedAt)}`;
}

export function getPatchConfirmation(
  response: ChannelEnvironmentPatchResponse,
) {
  if (response.requiresReplacement) {
    return {
      confirmLabel: "Apply Replacement",
      message:
        "This requires full environment replacement. All state will be reset. Continue?",
      title: "Confirm Replacement",
    };
  }
  return {
    confirmLabel: "Apply And Restart",
    message: `This change requires container restart. ${formatSpawnCount(response.runningSpawnsAffected)} will be stopped. Continue?`,
    title: "Confirm Restart",
  };
}

export function shortId(value: string | null): string {
  return value?.slice(0, 8) ?? "none";
}

/**
 * Collapse a long workspace path to `…/<short id>` so the volume row stays one
 * line; the full path is carried on the row's hover title. Short paths (e.g.
 * the in-container `/workspace`) pass through unchanged.
 */
export function shortWorkspacePath(path: string): string {
  const lastSegment = path.split("/").filter(Boolean).at(-1);
  if (!lastSegment || lastSegment.length <= 12) {
    return path;
  }
  return `…/${lastSegment.slice(0, 8)}`;
}

export function isRestartInFlight(environment: ChannelEnvironment): boolean {
  return (
    environment.status === "restarting" || environment.restartStartedAt !== null
  );
}

export function formatRelativeTime(value: string | null): string {
  if (!value) {
    return "unknown";
  }
  const seconds = Math.max(
    0,
    Math.round((Date.now() - new Date(value).getTime()) / 1000),
  );
  if (seconds < 60) {
    return `${seconds}s ago`;
  }
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  return `${Math.round(hours / 24)}d ago`;
}

export function formatElapsed(value: string | null): string {
  if (!value) {
    return "unknown";
  }
  const seconds = Math.max(
    0,
    Math.round((Date.now() - new Date(value).getTime()) / 1000),
  );
  if (seconds < 60) {
    return `${seconds}s`;
  }
  return `${Math.round(seconds / 60)}m`;
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(
    value,
  );
}

export function formatBytes(value: number): string {
  if (value < 1024) {
    return `${value} B`;
  }
  if (value < 1024 * 1024) {
    return `${formatNumber(value / 1024)} KB`;
  }
  return `${formatNumber(value / (1024 * 1024))} MB`;
}

export function formatSpawnCount(count: number): string {
  return `${count} running spawn${count === 1 ? "" : "s"}`;
}

/** Image references carry a full 64-hex `@sha256:` digest, which is a single
 *  unbreakable token: dropped raw into a 240px rail it either forces the pane to
 *  scroll sideways or wraps into three lines of hex. Twelve hex chars is the
 *  conventional short form (enough to eyeball against a registry); callers keep
 *  the full reference in a title attribute. */
export function shortenImageDigest(image: string): string {
  const [name, digest] = image.split("@sha256:");
  if (digest === undefined) {
    return image;
  }
  return `${name}@${digest.slice(0, 12)}…`;
}

export function sandboxSummaryLine(environment: ChannelEnvironment): string {
  const egress = getChannelEnvironmentEgressMode(environment.egressPolicy);
  const image = shortenImageDigest(
    environment.containerImage.split("/").at(-1) ?? "image",
  );
  return `${image} · ${environment.cpuCount} CPU · ${environment.ramMB} MB · egress ${egress}`;
}

export function liveUsageSummaryLine(environment: ChannelEnvironment): string {
  const spawns = environment.spawnAccounting?.totalSpawns ?? 0;
  // Same source of truth as the compute row's "freed at rest": once the
  // reservation's compute is released, an "N MB active" readout would lie.
  if (environment.reservationHeld?.computeReleasedAt != null) {
    return `at rest · ${formatNumber(spawns)} spawns`;
  }
  const activeRam = environment.reservationAccounting?.activeRamMB ?? 0;
  return `${formatNumber(activeRam)} MB active · ${formatNumber(spawns)} spawns`;
}

export function formatRepoCount(count: number): string {
  return `${count} ${count === 1 ? "repository" : "repositories"}`;
}

export function getRepoDetachMessage(
  repository: ChannelWorkspaceRepository,
  environment: ChannelEnvironment | null,
): string {
  const cannotCancel =
    " Detach cannot be cancelled in this release; the row stays visible until it completes.";
  if (environment && environment.status === "running") {
    return `Detach ${repository.remoteSlug}? The owner workspace removes the checkout once safety checks pass — uncommitted or unpushed work, or running spawns, hold it visibly pending instead.${cannotCancel}`;
  }
  return `Detach ${repository.remoteSlug}? The workspace is not running, so the checkout is removed the next time it starts or resumes.${cannotCancel}`;
}

/**
 * Absence acknowledgment is the one operator action that resolves an otherwise
 * permanent hold, so the confirmation states exactly what Arti can and cannot
 * prove — it is not a force detach.
 */
export function getRepoAcknowledgeAbsentDetachMessage(
  repository: ChannelWorkspaceRepository,
): string {
  return `The checkout for ${repository.remoteSlug} is absent on the current workspace runtime. Arti cannot prove what an earlier runtime held. Acknowledging completes only the control-plane detach: it does not delete or archive a checkout, and it is available only while the current runtime still proves the path is absent.`;
}

export function canForceRefreshRepository(reason: string | null): boolean {
  return reason === "dirty_tree" || reason === "non_ff";
}

export function shortSha(sha: string): string {
  return sha.slice(0, 7);
}
